/**
 * Signing in.
 *
 * Two panels above `lg`: the mahallu's own side on the left -- the green rail's
 * colour world, the masjid silhouette, an eight-pointed star lattice and the
 * hadith the dashboard opens with -- and the form on the right, on paper.
 *
 * The character is carried by geometry and by the building, not by imagery of
 * people, and the one line of Arabic script on the screen is the honorific in
 * the attribution, which the rest of the product already uses. Below `lg` the
 * green becomes a band across the top so the form still lands above the fold
 * on a phone.
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input, PasswordInput } from "@/components/ui/input";
import { IslamicPattern } from "@/features/auth/islamic-pattern";
import { ApiError } from "@/lib/api/client";
import { useSession } from "@/lib/auth/session";
import { setLocale, type Locale } from "@/lib/i18n";

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { t: td } = useTranslation("dashboards");
  const { signIn } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const locale: Locale = i18n.language.startsWith("ml") ? "ml" : "en";

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    // Stop here rather than posting a blank form. Sign-in is rate limited to
    // five attempts a minute per address, and an empty submission was taking
    // one of them and coming back as a 422 -- so a fumbled password could
    // lock someone out of their own office.
    if (!email.trim() || !password) {
      setErrorCode("SIGN_IN_INCOMPLETE");
      return;
    }
    setBusy(true);
    setErrorCode(null);
    try {
      await signIn(email, password);
    } catch (error) {
      setErrorCode(error instanceof ApiError ? error.code : "NETWORK");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[1.05fr_1fr]">
      {/* ---------------------------------------------------- the mahallu */}
      <aside className="bg-sidebar-gloss relative flex flex-col overflow-hidden px-6 py-8 sm:px-10 lg:px-14 lg:py-12">
        {/* The lattice, faint enough to be texture rather than decoration. */}
        <IslamicPattern className="pointer-events-none absolute inset-0 h-full w-full text-white/[0.07]" />

        <div className="relative">
          <span className="tabular inline-grid size-11 place-items-center rounded-xl bg-white text-sm font-semibold text-sidebar-chip-ink">
            ANK
          </span>
          <h1 className="font-display mt-5 text-3xl leading-tight font-medium tracking-tight text-sidebar-ink lg:text-4xl">
            {t("app.name")}
          </h1>
          <p className="mt-1 text-md text-sidebar-ink-muted">{t("app.sub")}</p>
        </div>

        {/* The hadith the dashboard opens with, so the product says the same
            thing at the door as it does inside. Hidden on a phone, where the
            band has to stay short enough to keep the form above the fold. */}
        <figure className="relative mt-auto hidden max-w-md lg:block">
          <blockquote className="font-display text-lg leading-relaxed text-sidebar-ink italic">
            {td("hero.quote")}
          </blockquote>
          <figcaption className="mt-2 text-sm text-sidebar-ink-subtle">
            {td("hero.quoteBy")}
          </figcaption>
        </figure>

        {/* The silhouette sits on the foot of the panel, as it does on the
            rail. Absolutely positioned so it can never push the content. */}
        <img
          src="/sidebar-masjid.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 hidden w-full opacity-30 [filter:brightness(0)_invert(1)] lg:block"
        />
      </aside>

      {/* ------------------------------------------------------- the form */}
      <main className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-2xl leading-tight font-medium tracking-tight">
            {t("action.signIn")}
          </h2>
          <p className="mt-1 text-md text-ink-muted">{t("app.tagline")}</p>

          <form
            onSubmit={(event) => void onSubmit(event)}
            className="mt-7 space-y-5"
            noValidate
          >
            {errorCode ? (
              <div
                role="alert"
                className="rounded-sm bg-danger-soft px-3 py-2 text-sm text-danger-soft-ink"
              >
                {t([`errors.${errorCode}`, "errors.UNKNOWN_ERROR"])}
              </div>
            ) : null}

            <div>
              <label
                htmlFor="email"
                className="mb-2 block text-xs font-medium text-ink"
              >
                {t("field.loginId")}
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-xs font-medium text-ink"
              >
                {t("field.password")}
              </label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={busy}
            >
              {t("action.signIn")}
            </Button>
          </form>

          {/* Two languages, so a toggle beats a menu. Set before signing in,
              because the person who needs Malayalam needs it on this screen. */}
          <div className="mt-8 flex items-center justify-center gap-1">
            {(["en", "ml"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setLocale(option)}
                aria-pressed={locale === option}
                className={
                  locale === option
                    ? "rounded-md bg-surface-sunken px-3 py-1.5 text-xs font-medium text-ink"
                    : "rounded-md px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink"
                }
              >
                {t(`language.${option}`)}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
